// src/pages/admin/AdminStats.js
// Statistiques détaillées (admin) - NE PAS MODIFIER

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ROUTES from '../../constants/routes';
import { formatPrice } from '../../utils/formatPrice';

const AdminStats = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    // Données de démo (à remplacer par adminStatsAPI.getDashboard())
    const demoStats = {
      totalListings: 42,
      publishedListings: 30,
      pendingListings: 8,
      draftListings: 4,
      totalReservations: 25,
      newReservations: 5,
      confirmedReservations: 15,
      cancelledReservations: 5,
      totalUsers: 156,
      newUsersThisMonth: 23,
      totalRevenue: 1250000,
      byCategoryListings: [
        { category: 'Locations', count: 18, color: '#48BB78' },
        { category: 'Séjours', count: 12, color: '#4299E1' },
        { category: 'Immobilier', count: 8, color: '#FFD700' },
        { category: 'Services', count: 4, color: '#ED8936' },
      ],
      byCityListings: [
        { city: 'Douala', count: 28 },
        { city: 'Yaoundé', count: 10 },
        { city: 'Bafoussam', count: 3 },
        { city: 'Kumba', count: 1 },
      ],
    };
    setStats(demoStats);
  }, []);

  if (!stats) return null;

  const maxCategoryCount = Math.max(...stats.byCategoryListings.map((c) => c.count));
  const maxCityCount = Math.max(...stats.byCityListings.map((c) => c.count));

  const styles = {
    container: { display: 'flex', flexDirection: 'column', minHeight: '100vh', maxWidth: '900px', margin: '0 auto', backgroundColor: '#0A0E1A' },
    header: { display: 'flex', alignItems: 'center', gap: '12px', padding: '16px 20px', borderBottom: '1px solid rgba(255, 255, 255, 0.1)', position: 'sticky', top: 0, backgroundColor: '#0A0E1A', zIndex: 50 },
    backButton: { background: 'none', border: 'none', color: '#FFFFFF', fontSize: '20px', cursor: 'pointer' },
    title: { fontSize: '20px', fontWeight: '700', color: '#FFFFFF' },
    statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', padding: '20px' },
    statCard: { padding: '16px', backgroundColor: '#162033', borderRadius: '12px', border: '1px solid rgba(255, 255, 255, 0.1)', display: 'flex', flexDirection: 'column', gap: '6px' },
    statValue: { fontSize: '24px', fontWeight: '700', color: '#FFD700' },
    statLabel: { fontSize: '12px', color: '#A0AEC0' },
    sectionTitle: { fontSize: '16px', fontWeight: '600', color: '#FFFFFF', padding: '8px 20px 12px' },
    chartCard: { margin: '0 20px 20px', padding: '16px', backgroundColor: '#162033', borderRadius: '12px', border: '1px solid rgba(255, 255, 255, 0.1)' },
    barRow: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' },
    barLabel: { fontSize: '13px', color: '#A0AEC0', width: '90px', flexShrink: 0 },
    barTrack: { flex: 1, height: '10px', backgroundColor: '#111827', borderRadius: '5px', overflow: 'hidden' },
    barFill: { height: '100%', borderRadius: '5px' },
    barValue: { fontSize: '13px', fontWeight: '600', color: '#FFFFFF', width: '30px', textAlign: 'right' },
  };

  return (
    <div style={styles.container}>
      <Helmet>
        <title>Statistiques | Fynho Admin</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div style={styles.header}>
        <button style={styles.backButton} onClick={() => navigate(ROUTES.ADMIN_DASHBOARD)}>←</button>
        <div style={styles.title}>Statistiques</div>
      </div>

      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <div style={styles.statValue}>{stats.totalListings}</div>
          <div style={styles.statLabel}>Annonces totales</div>
        </div>
        <div style={styles.statCard}>
          <div style={{ ...styles.statValue, color: '#48BB78' }}>{stats.publishedListings}</div>
          <div style={styles.statLabel}>Publiées</div>
        </div>
        <div style={styles.statCard}>
          <div style={{ ...styles.statValue, color: '#ED8936' }}>{stats.pendingListings}</div>
          <div style={styles.statLabel}>En attente</div>
        </div>
        <div style={styles.statCard}>
          <div style={{ ...styles.statValue, color: '#4299E1' }}>{stats.totalReservations}</div>
          <div style={styles.statLabel}>Réservations</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statValue}>{stats.totalUsers}</div>
          <div style={styles.statLabel}>Utilisateurs</div>
        </div>
        <div style={styles.statCard}>
          <div style={{ ...styles.statValue, color: '#48BB78' }}>+{stats.newUsersThisMonth}</div>
          <div style={styles.statLabel}>Nouveaux ce mois</div>
        </div>
      </div>

      <div style={styles.sectionTitle}>Annonces par catégorie</div>
      <div style={styles.chartCard}>
        {stats.byCategoryListings.map((item) => (
          <div key={item.category} style={styles.barRow}>
            <span style={styles.barLabel}>{item.category}</span>
            <div style={styles.barTrack}>
              <div
                style={{
                  ...styles.barFill,
                  width: `${(item.count / maxCategoryCount) * 100}%`,
                  backgroundColor: item.color,
                }}
              />
            </div>
            <span style={styles.barValue}>{item.count}</span>
          </div>
        ))}
      </div>

      <div style={styles.sectionTitle}>Annonces par ville</div>
      <div style={styles.chartCard}>
        {stats.byCityListings.map((item) => (
          <div key={item.city} style={styles.barRow}>
            <span style={styles.barLabel}>{item.city}</span>
            <div style={styles.barTrack}>
              <div
                style={{
                  ...styles.barFill,
                  width: `${(item.count / maxCityCount) * 100}%`,
                  backgroundColor: '#FFD700',
                }}
              />
            </div>
            <span style={styles.barValue}>{item.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminStats;